import type { ReactNode } from "react";
import Sparkline from "./Sparkline";
import { signedClass } from "../lib/format";

// One KPI tile. Big number + optional sub-label + optional sparkline.
//
// `value` is rendered as-is — caller pre-formats with formatINR/formatInt/etc.
// `tone` controls coloring on the value: 'pnl' colors by sign of `valueRaw`.

type Tone = "neutral" | "pnl" | "brand";

interface Props {
  label: ReactNode;
  value: ReactNode;
  valueRaw?: number; // numeric raw for sign-based coloring
  sub?: ReactNode;
  tone?: Tone;
  sparkline?: number[];
  icon?: ReactNode;
  href?: string;
  onClick?: () => void;
}

export default function KpiTile({
  label,
  value,
  valueRaw,
  sub,
  tone = "neutral",
  sparkline,
  icon,
}: Props) {
  const valueColor =
    tone === "pnl"
      ? signedClass(valueRaw ?? 0)
      : tone === "brand"
        ? "text-brand-600 dark:text-brand-400"
        : "text-fg";
  return (
    <div className="bg-surface border border-border rounded-lg p-4 shadow-soft flex flex-col gap-1">
      <div className="flex items-start justify-between text-xs text-muted uppercase tracking-wide">
        <span>{label}</span>
        {icon && <span className="text-fg-subtle/70">{icon}</span>}
      </div>
      <div className={`text-2xl font-semibold font-mono leading-tight ${valueColor}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-fg-subtle">{sub}</div>}
      {sparkline && sparkline.length > 0 && (
        <div className="mt-2">
          <Sparkline values={sparkline} />
        </div>
      )}
    </div>
  );
}
